// Projects synced in from external calendars (e.g. Google Calendar) — treated
// like temp/Marked events: distinct color, always editable regardless of approval_status.
export const SYNC_PROJECT_IDS = new Set(['174777000004247495', '174777000006587014']);

// A draft ("Draft Timelog") is a log the Google Calendar sync or the Claude skill
// has filed but nobody has confirmed yet. Zoho reads it as `draft2` ("true"), and
// the key is missing when it was never set. The write column is UDF_BOOLEAN3.
export function isDraftLog(log) {
  const parse = (v) => v === true || v === 1 || (typeof v === 'string' && ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase()));
  if (parse(log?.draft2) || parse(log?.UDF_BOOLEAN3)) return true;
  const cf = log?.custom_fields;
  if (Array.isArray(cf)) {
    return parse(cf.find((f) => f?.column_name === 'UDF_BOOLEAN3' || f?.api_name === 'draft2')?.value);
  }
  return !!cf && typeof cf === 'object' && parse(cf.UDF_BOOLEAN3);
}

// Which logs may change project in the form. Sync-project logs always could.
// A draft on any project can too, because automation picks the customer project
// itself and can pick wrong. Zoho cannot move a log, so a changed project means
// delete + recreate (handleReassignSyncEvent). Approved and People logs never.
export function canChangeProject(event) {
  if (!event || event.isPeople || event.approval_status === 'Approved') return false;
  return SYNC_PROJECT_IDS.has(event.projectId) || !!event.draft;
}

// Save from the form: delete + recreate instead of update in place. Sync-project
// logs always (the recreate is what confirms them). A draft elsewhere only when
// the project actually changed, otherwise update-timelog saves it in place.
export function needsReassign(event, newProjectId) {
  if (SYNC_PROJECT_IDS.has(event?.projectId)) return true;
  return canChangeProject(event) && newProjectId !== event.projectId;
}

// Dark-mode calendar colors keyed by entry type, matching the type dropdown's
// hues (blue/green/yellow/purple/gray). Each type has a "light" shade (unapproved)
// and a "normal" shade (approved) so approval status still reads at a glance.
export const TYPE_COLORS = {
  'Udvikling': {
    normal: { backgroundColor: '#1B4F91', borderColor: '#2E74C7' },
    light: { backgroundColor: '#3D77B8', borderColor: '#62A0DE' },
  },
  'Møde': {
    normal: { backgroundColor: '#1E6B41', borderColor: '#2E9760' },
    light: { backgroundColor: '#3C9163', borderColor: '#5CBE87' },
  },
  'Support': {
    normal: { backgroundColor: '#8A6D00', borderColor: '#B08F00' },
    light: { backgroundColor: '#B4923A', borderColor: '#D4B562' },
  },
  'Business Development': {
    normal: { backgroundColor: '#6A2E91', borderColor: '#8F4DBF' },
    light: { backgroundColor: '#9558B8', borderColor: '#B87ADC' },
  },
  'Workshop': {
    normal: { backgroundColor: '#4A4A4E', borderColor: '#6E6E73' },
    light: { backgroundColor: '#6E6E73', borderColor: '#8E8E93' },
  },
};
export const DEFAULT_TYPE_COLOR = {
  normal: { backgroundColor: '#3A3740', borderColor: '#5A5760' },
  light: { backgroundColor: '#565260', borderColor: '#78748A' },
};

export const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// Transform Zoho Projects timelogs to calendar events
export function transformTimelogsToEvents(apiData) {
    if (!apiData || !apiData.timelogs || !apiData.timelogs.date) return [];
    if (!apiData.timelogs.date.length) return [];

    const events = apiData.timelogs.date.flatMap((day) => {

      const logs = day.tasklogs || day.generallogs || [];
      const date = new Date(day.date_long);

      return logs.map((log) => {
        let start, end;

        if (log.start_time && log.end_time) {
          const startTime = log.start_time.split(':');
          const endTime = log.end_time.split(':');
          start = new Date(date);
          start.setHours(startTime[0], startTime[1]);
          end = new Date(date);
          end.setHours(endTime[0], endTime[1]);
          if (end <= start) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
          }
          const durationHours = (end - start) / (1000 * 60 * 60);
          if (durationHours > 23) {
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000);
          }
        } else {
          start = new Date(date);
          start.setHours(4, 0);
          const durationMinutes = parseInt(log.total_minutes) || 0;
          if (durationMinutes > 23 * 60) {
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000);
          } else {
            end = new Date(start.getTime() + durationMinutes * 60000);
          }
        }

        // Extract "Type" from custom_fields (UDF_CHAR4) or fallback
        const extractType = (cf) => {
          if (!cf) return '';
          // Array shape
          if (Array.isArray(cf)) {
            const hit = cf.find(
              f =>
                f?.column_name === 'UDF_CHAR4' ||
                (typeof f?.label_name === 'string' && f.label_name.toLowerCase() === 'type')
            );
            return hit?.value || '';
          }
          // Object shape
          if (typeof cf === 'object') {
            return cf.UDF_CHAR4 || cf.Type || '';
          }
          return '';
        };
        const type =
          extractType(log.custom_fields) ||
          log.timesheet_cf_0001 || // fallback if present
          '';

        const extractAkut = (cf) => {
          const parseZohoBool = (v) => {
            if (typeof v === 'boolean') return v;
            if (typeof v === 'number') return v === 1;
            if (typeof v === 'string') {
              const s = v.trim().toLowerCase();
              return s === 'true' || s === '1' || s === 'yes' || s === 'on';
            }
            return false;
          };

          if (!cf) return false;

          // Array shape
          if (Array.isArray(cf)) {
            const hit = cf.find(f =>
              f?.column_name === 'UDF_BOOLEAN1' ||
              (typeof f?.label_name === 'string' && f.label_name.toLowerCase() === 'akut')
            );
            return parseZohoBool(hit?.value);
          }

          // Object shape
          if (typeof cf === 'object') {
            return parseZohoBool(cf.UDF_BOOLEAN1);
          }

          return false;
        };
        const akut = extractAkut(log.custom_fields) || !!log.UDF_BOOLEAN1;

        const timecodeId = log.task?.id_string || '';
        const timecodeName = log.task?.name || '';
        

        const displayTitle = (log.notes || log.name || log.log_name || '');

        const event = {
          title: displayTitle,
          start,
          end,
          projectId: log.project?.id_string || '',
          timecodeId, 
          projectName: log.project?.name || '',
          timecodeName,
          approval_status: log.approval_status || '',
          billingtype: log.bill_status || '',
          logId: `projects:${log.id_string || log.id || ''}`,
          type,
          akut,
          draft: isDraftLog(log),
          module: log.type || 'general', // general, task or issue: edit and delete need it
        };

        return event;
      });
    });

    return events;
  }

// Transform a Zoho People timelog to a calendar event
export function handlePeopleTimelogToEvent(timelogDetails) {
    if (!timelogDetails.timelogId) return null;
    const date = new Date(timelogDetails.workDate);

    // Prefer explicit time fields; be resilient to API variants
    const getHM = (val) => {
      if (!val) return null;
      // Accept "HH:mm", "HH:mm:ss", or "HH:mm AM/PM"
      const ampm = /am|pm/i.test(val);
      let [h, m, s] = val.replace(/\s?(AM|PM)$/i, '').split(':');
      let hour = parseInt(h, 10);
      const min = parseInt(m || '0', 10);
      if (ampm) {
        const isPM = /pm/i.test(val);
        if (isPM && hour < 12) hour += 12;
        if (!isPM && hour === 12) hour = 0;
      }
      return { hour, min };
    };

    const fromRaw = timelogDetails.fromTimeInTimeFormat || timelogDetails.fromTime || timelogDetails.fromTimeInHrs;
    const toRaw   = timelogDetails.toTimeInTimeFormat   || timelogDetails.toTime   || timelogDetails.toTimeInHrs;

    let start = new Date(date);
    let end = new Date(date);

    const fromHM = getHM(fromRaw);
    const toHM = getHM(toRaw);

    if (fromHM && toHM) {
      start.setHours(fromHM.hour, fromHM.min, 0, 0);
      end.setHours(toHM.hour, toHM.min, 0, 0);
    } else {
      // Fallback using duration
      const mins = timelogDetails.hoursInMins
        ?? (typeof timelogDetails.hours === 'number' ? Math.round(timelogDetails.hours * 60) : 60);
      start.setHours(4, 0, 0, 0);
      end = new Date(start.getTime() + mins * 60000);
    }

    return {
      title: timelogDetails.description || timelogDetails.jobName || '',
      start,
      end,
      projectId: '',
      timecodeId: timelogDetails.jobId || '',
      projectName: '',
      timecodeName: timelogDetails.jobName || '',
      approval_status: timelogDetails.approvalStatus || '',
      billingtype: timelogDetails.billingStatus === 'billable' ? 'Billable' : 'Non Billable',
      logId: `people:${timelogDetails.timelogId}`,
      isPeople: true,
    };
  }